import { CANONICAL_HOST } from "@/config";

export type HelpHeadingTocItem = {
  id: string;
  text: string;
  level: 2 | 3;
};

const HEADING_RE = /<h([23])(\s[^>]*)?>([\s\S]*?)<\/h\1>/gi;

export const stripHelpHtmlToText = (html: string) =>
  html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const FIRST_PARAGRAPH_RE = /<p(\s[^>]*)?>([\s\S]*?)<\/p>/i;

const normaliseForComparison = (text: string) =>
  text
    .replace(/&[#a-z0-9]+;/gi, " ")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "");

/**
 * True when the lead/excerpt is just the body's first paragraph (in full or
 * truncated), so rendering it above the body would show it twice (WHO-6056).
 */
export const helpLeadRepeatsBody = (lead: string, bodyHtml: string) => {
  const match = FIRST_PARAGRAPH_RE.exec(bodyHtml);
  const leadText = normaliseForComparison(lead);
  const firstParagraph = normaliseForComparison(
    stripHelpHtmlToText(match?.[2] ?? "")
  );

  if (!leadText || !firstParagraph) {
    return false;
  }

  return (
    firstParagraph.startsWith(leadText) || leadText.startsWith(firstParagraph)
  );
};

export const slugifyHelpHeading = (text: string): string => {
  const slug = text
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return slug || "section";
};

const uniqueHeadingId = (base: string, usedIds: Set<string>) => {
  let id = base;
  let suffix = 2;

  while (usedIds.has(id)) {
    id = `${base}-${suffix}`;
    suffix += 1;
  }

  usedIds.add(id);

  return id;
};

export const injectHelpHeadingIds = (
  html: string
): { html: string; headings: HelpHeadingTocItem[] } => {
  const usedIds = new Set<string>();
  const headings: HelpHeadingTocItem[] = [];

  const next = html.replace(HEADING_RE, (_match, level, rawAttrs, inner) => {
    const attrs = typeof rawAttrs === "string" ? rawAttrs : "";
    const text = stripHelpHtmlToText(inner);
    const existing = attrs.match(/\sid=["']([^"']+)["']/i);
    const base = existing?.[1] ? existing[1] : slugifyHelpHeading(text);
    const id = uniqueHeadingId(base, usedIds);
    const attrsWithoutId = attrs.replace(/\s*id=["'][^"']*["']/i, "");
    const numericLevel: 2 | 3 = Number(level) === 3 ? 3 : 2;

    headings.push({ id, text, level: numericLevel });

    return `<h${level}${attrsWithoutId} id="${id}">${inner}</h${level}>`;
  });

  return { html: next, headings };
};

export const relativiseHelpLinks = (
  html: string,
  canonicalHost = CANONICAL_HOST
): string => {
  const escapedHost = canonicalHost.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  return html.replace(
    new RegExp(`href="${escapedHost}((?:/[a-z]{2})?/help/[^"]*)"`, "gi"),
    'href="$1"'
  );
};

/**
 * Help-service rewrites legacy Elevio hrefs to bare `/help/...` paths. Inside a
 * `dangerouslySetInnerHTML` body Next adds no locale prefix, so a Dutch page
 * would otherwise jump to the English article. Prefix them here.
 */
export const localiseHelpLinks = (
  html: string,
  locale: string | undefined,
  defaultLocale = "en"
): string => {
  if (!locale || locale === defaultLocale) {
    return html;
  }

  return html.replace(
    /href="\/help(\/[^"]*)?"/gi,
    (_match, rest) => `href="/${locale}/help${rest ?? ""}"`
  );
};

export const wrapHelpTables = (html: string): string =>
  html.replace(/<table\b[\s\S]*?<\/table>/gi, table => {
    if (table.includes("overflow-x-auto")) {
      return table;
    }

    return `<div class="overflow-x-auto">${table}</div>`;
  });

export const enhanceHelpImages = (html: string): string =>
  html.replace(/<img\b([^>]*?)(\/?)>/gi, (_match, rawAttrs, closing) => {
    let attrs = typeof rawAttrs === "string" ? rawAttrs : "";

    if (!/\bloading\s*=/i.test(attrs)) {
      attrs += ' loading="lazy"';
    }

    if (!/\bclass\s*=/i.test(attrs)) {
      attrs += ' class="max-w-full rounded-[4px]"';
    }

    return `<img${attrs}${closing}>`;
  });

export const enhanceHelpArticleHtml = (
  html: string,
  canonicalHost = CANONICAL_HOST,
  locale?: string
): { html: string; headings: HelpHeadingTocItem[] } => {
  const withHeadings = injectHelpHeadingIds(html);
  const withLinks = localiseHelpLinks(
    relativiseHelpLinks(withHeadings.html, canonicalHost),
    locale
  );
  const withTables = wrapHelpTables(withLinks);
  const withImages = enhanceHelpImages(withTables);

  return { html: withImages, headings: withHeadings.headings };
};
